import { Fragment, useMemo, useState } from "react";

import { MobileDrawer } from "../app/CollapsiblePanel";
import { DegradedBanner } from "../app/DegradedBanner";
import { formatDeviation, formatDuration } from "../app/format";
import { usePersistentString } from "../app/persistentUi";
import { normalizeSearch } from "../app/search";
import { StreamBadge, Topbar, TopbarClock } from "../app/Topbar";
import { useStreamContext } from "../app/StreamProvider";
import { useBackend } from "../app/BackendProvider";
import { useIsMobile } from "../app/useIsMobile";
import { AsyncBlock } from "../admin/bits";
import { useNetworkId, useThresholds } from "../config/ConfigProvider";
import { useResource } from "../app/useResource";
import type { ServiceGap, ServicePlanResponse, ServiceTimelinePrestation, ServiceTimelineRow } from "../contract/types";

/**
 * Onglet Services (CDC §5.8, P2-52). Hors régulation : « articuler les infos
 * qu'on a sur les services conducteurs » — n° de service + sortie + n° de bus,
 * **jamais l'agent**. Forme retenue (décision P2-51) : un **diagramme de Gantt**
 * de la journée (`/services/timeline`), plus le plan détaillé du service
 * sélectionné (`/services/{group}/{number}` — trous qualifiés, relèves, live).
 */
export function ServicesScreen() {
  const { status } = useStreamContext();
  const backend = useBackend();
  const networkId = useNetworkId();
  const isMobile = useIsMobile();

  const today = new Date().toISOString().slice(0, 10);
  const [pickedDay, setPickedDay] = usePersistentString("sentinelle.services.day");
  const day = pickedDay || today;
  const [selected, setSelected] = useState<string | null>(null); // `${group}/${number}`
  const [groupFilter, setGroupFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [zoomPct, setZoomPct] = useState(100);
  const [tickHours, setTickHours] = useState<1 | 2 | 5>(2);
  const [popin, setPopin] = useState<{ row: ServiceTimelineRow; prestation: ServiceTimelinePrestation } | null>(null);

  const timelineQuery = useResource(
    (signal) => backend.api.getServicesTimeline(networkId, { date: day }, signal),
    [backend, networkId, day],
  );
  const timeline = timelineQuery.data;
  const allRows = timeline?.rows ?? [];
  const groupOptions = useMemo(() => [...new Set(allRows.map((r) => r.groupLetters))].sort(), [allRows]);
  const groupFiltered = groupFilter ? allRows.filter((r) => r.groupLetters === groupFilter) : allRows;
  // Recherche par n° de ligne (n'importe quelle prestation-course du service)
  // ou par n° de service — sur la journée choisie ci-dessus (demande utilisateur).
  const term = normalizeSearch(search);
  const rows = term
    ? groupFiltered.filter(
        (r) => normalizeSearch(r.serviceNumber).includes(term) || r.prestations.some((p) => normalizeSearch(p.ligneRaw).includes(term)),
      )
    : groupFiltered;

  const planQuery = useResource(
    (signal) => {
      if (!selected) return Promise.resolve(null);
      const [g, n] = selected.split("/");
      return backend.api.getServicePlan(networkId, g!, n!, { date: day }, signal);
    },
    [backend, networkId, day, selected],
  );

  return (
    <div className="mae-app">
      <Topbar
        current="services"
        right={
          <>
            <StreamBadge status={status} />
            <TopbarClock />
          </>
        }
      />
      <DegradedBanner />

      <div className="mae-fill mae-scroll-y" style={{ padding: "20px 24px", background: "var(--sentinelle-surface)" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          <h2 style={{ font: "700 15px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)" }}>
            Services conducteurs{timeline?.period ? ` — régime ${timeline.period}` : ""}
          </h2>
          <input
            type="date"
            className="input is-small"
            style={{ width: 160 }}
            value={day}
            onChange={(e) => {
              setPickedDay(e.target.value || null);
              setSelected(null);
            }}
          />
          {day === today ? <span className="tag is-link is-light">journée en cours</span> : null}
          {groupOptions.length > 1 ? (
            <div className="select is-small">
              <select
                aria-label="Filtrer par groupe"
                value={groupFilter}
                onChange={(e) => {
                  setGroupFilter(e.target.value);
                  setSelected(null);
                }}
              >
                <option value="">Tous les groupes</option>
                {groupOptions.map((g) => (
                  <option key={g} value={g}>
                    Groupe {g}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <input
            type="search"
            className="input is-small"
            style={{ width: 170 }}
            placeholder="N° de ligne ou de service…"
            aria-label="Rechercher par numéro de ligne ou de service"
            autoComplete="off"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelected(null);
            }}
          />
          <span className="mono" style={{ fontSize: "0.7rem", color: "var(--sentinelle-muted)" }}>
            {rows.length} service{rows.length > 1 ? "s" : ""}
            {timeline?.truncated ? " (liste tronquée à 500)" : ""}
          </span>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div className="buttons has-addons" style={{ marginBottom: 0 }} role="group" aria-label="Amplitude des graduations">
              {([1, 2, 5] as const).map((h) => (
                <button
                  key={h}
                  type="button"
                  className={`button is-small${tickHours === h ? " is-link" : ""}`}
                  aria-pressed={tickHours === h}
                  onClick={() => setTickHours(h)}
                >
                  {h} h
                </button>
              ))}
            </div>
            <div className="buttons has-addons" style={{ marginBottom: 0 }} role="group" aria-label="Zoom du diagramme">
              <button
                type="button"
                className="button is-small"
                aria-label="Dézoomer"
                disabled={zoomPct <= 50}
                onClick={() => setZoomPct((z) => Math.max(50, z - 25))}
              >
                −
              </button>
              <button type="button" className="button is-small" disabled style={{ minWidth: 52 }}>
                {zoomPct}%
              </button>
              <button
                type="button"
                className="button is-small"
                aria-label="Zoomer"
                disabled={zoomPct >= 300}
                onClick={() => setZoomPct((z) => Math.min(300, z + 25))}
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Split diagramme / détail (dump 2026-09-10) : le plan du service
            sélectionné s'ouvre à côté du Gantt plutôt que dessous, chacun avec
            son propre défilement. Mobile (2026-09-11) : plus de colonne à 45%
            (illisible sous 400px) — le détail s'ouvre en tiroir plein écran. */}
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ flex: selected && !isMobile ? "1 1 55%" : "1 1 100%", minWidth: 0 }}>
            <AsyncBlock
              loading={timelineQuery.loading && !timelineQuery.data}
              error={timelineQuery.error}
              onRetry={timelineQuery.reload}
              empty={rows.length === 0}
              emptyLabel={
                term
                  ? `Aucun service ne correspond à « ${search} » ce jour-là.`
                  : "Aucun service pour cette journée (pas de document « services conducteurs » importé ?)."
              }
            >
              <ServiceGantt
                rows={rows}
                selected={selected}
                onSelect={(k) => setSelected((cur) => (cur === k ? null : k))}
                zoomPct={zoomPct}
                tickHours={tickHours}
                onBarClick={(row, prestation) => setPopin({ row, prestation })}
              />
            </AsyncBlock>
          </div>

          {selected && !isMobile ? (
            <div style={{ flex: "1 1 45%", minWidth: 0, maxHeight: "calc(100vh - 190px)", overflowY: "auto" }}>
              <AsyncBlock loading={planQuery.loading && !planQuery.data} error={planQuery.error} onRetry={planQuery.reload}>
                {planQuery.data ? <ServiceDetail plan={planQuery.data} isToday={day === today} /> : <span />}
              </AsyncBlock>
            </div>
          ) : null}
        </div>

        {selected && isMobile ? (
          <MobileDrawer side="right" onClose={() => setSelected(null)}>
            <AsyncBlock loading={planQuery.loading && !planQuery.data} error={planQuery.error} onRetry={planQuery.reload}>
              {planQuery.data ? <ServiceDetail plan={planQuery.data} isToday={day === today} /> : <span />}
            </AsyncBlock>
          </MobileDrawer>
        ) : null}

        {popin ? <PrestationPopin row={popin.row} prestation={popin.prestation} onClose={() => setPopin(null)} /> : null}
      </div>
    </div>
  );
}

// ── Gantt ──────────────────────────────────────────────────────────────────

function ServiceGantt({
  rows,
  selected,
  onSelect,
  zoomPct,
  tickHours,
  onBarClick,
}: {
  rows: ServiceTimelineRow[];
  selected: string | null;
  onSelect: (key: string) => void;
  /** Zoom horizontal (dump 2026-09-10), 50-300 %, 100 % = largeur historique. */
  zoomPct: number;
  /** Amplitude des graduations horaires : 1 h / 2 h / 5 h (dump 2026-09-10). */
  tickHours: 1 | 2 | 5;
  onBarClick: (row: ServiceTimelineRow, prestation: ServiceTimelinePrestation) => void;
}) {
  // v1.53 — le seuil « coupure » n'est plus une constante à 40 min : posé sur
  // `thresholds.driverSplitShiftSeconds` (configurable à chaud, défaut 2 h).
  const thresholds = useThresholds();
  const bounds = useMemo(() => {
    let lo = 6 * 3600;
    let hi = 21 * 3600;
    for (const r of rows) {
      for (const p of r.prestations) {
        if (p.startSeconds != null) lo = Math.min(lo, p.startSeconds);
        if (p.endSeconds != null) hi = Math.max(hi, p.endSeconds);
      }
    }
    lo = Math.max(3 * 3600, Math.floor(lo / 3600) * 3600);
    hi = Math.min(30 * 3600, Math.ceil(hi / 3600) * 3600);
    return { lo, hi };
  }, [rows]);

  const W = Math.round(940 * (zoomPct / 100));
  const LABEL_W = 118;
  const H_ROW = 26;
  const span = Math.max(1, bounds.hi - bounds.lo);
  const x = (sec: number) => LABEL_W + ((sec - bounds.lo) / span) * W;
  const height = rows.length * H_ROW + 26;

  const hourTicks: number[] = [];
  for (let h = Math.ceil(bounds.lo / 3600); h * 3600 <= bounds.hi; h += tickHours) hourTicks.push(h);

  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--sentinelle-rule)", background: "#fff" }}>
      <svg width={W + LABEL_W + 16} height={height} role="img" aria-label="Diagramme de Gantt des services" style={{ display: "block" }}>
        {hourTicks.map((h) => (
          <g key={h}>
            <line x1={x(h * 3600)} y1={16} x2={x(h * 3600)} y2={height} stroke="var(--sentinelle-rule)" />
            <text x={x(h * 3600) + 2} y={12} fontSize={9} fill="var(--sentinelle-muted)">
              {String(h % 24).padStart(2, "0")} h
            </text>
          </g>
        ))}

        {rows.map((r, i) => {
          const key = `${r.groupLetters}/${r.serviceNumber}`;
          const y = 22 + i * H_ROW;
          const isSel = key === selected;
          return (
            <g key={key} onClick={() => onSelect(key)} style={{ cursor: "pointer" }}>
              <rect x={0} y={y} width={W + LABEL_W} height={H_ROW} fill={isSel ? "#e8f0f8" : "transparent"} />
              <text x={6} y={y + H_ROW / 2 + 4} fontSize={11} fontWeight={700} fontFamily="var(--bulma-family-code)" fill="var(--sentinelle-ink)">
                {r.groupLetters} {r.serviceNumber}
              </text>
              <text x={44} y={y + H_ROW / 2 + 4} fontSize={9} fontFamily="var(--bulma-family-code)" fill="var(--sentinelle-muted)">
                {fmtHm(r.workTimeSeconds ?? 0)}
              </text>

              {r.prestations.map((p, k) => {
                if (p.startSeconds == null || p.endSeconds == null) return null;
                const bx = x(p.startSeconds);
                const bw = Math.max(2, x(p.endSeconds) - x(p.startSeconds));
                // J.10 — texte DANS la barre dès qu'elle est assez large :
                // n° de ligne pour une course, sinon abréviation du type.
                const inBar = p.kind?.startsWith("course")
                  ? (p.ligneRaw ?? "").slice(0, 6)
                  : segShort(p.kind);
                return (
                  <g
                    key={k}
                    // Dump 2026-09-10 : clic sur une barre = pop-in de détail de
                    // CETTE prestation, distinct du clic sur la ligne (sélection
                    // du service entier) — d'où le `stopPropagation`.
                    onClick={(e) => {
                      e.stopPropagation();
                      onBarClick(r, p);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <rect
                      x={bx}
                      y={y + 5}
                      width={bw}
                      height={H_ROW - 12}
                      rx={2}
                      fill={segColor(p)}
                      stroke={p.kind?.startsWith("course") ? "none" : "var(--sentinelle-rule)"}
                      strokeDasharray={p.kind === "deplacement" ? "2 2" : undefined}
                    >
                      <title>{titlePrestation(p)}</title>
                    </rect>
                    {bw >= 22 && inBar ? (
                      <text
                        x={bx + bw / 2}
                        y={y + H_ROW / 2 + 3}
                        fontSize={8}
                        fontFamily="var(--bulma-family-code)"
                        fontWeight={700}
                        textAnchor="middle"
                        fill={p.kind?.startsWith("course") ? "#fff" : "var(--sentinelle-ink-2)"}
                        pointerEvents="none"
                        style={{ paintOrder: "stroke", stroke: p.kind?.startsWith("course") ? "rgba(0,0,0,.35)" : "none", strokeWidth: 2 }}
                      >
                        {inBar}
                      </text>
                    ) : null}
                  </g>
                );
              })}

              {r.gaps
                .filter((g) => g.kind === "coupure")
                .map((g, k) => {
                  const p = r.prestations[g.afterIndex];
                  if (!p?.endSeconds) return null;
                  return (
                    <line
                      key={`gap${k}`}
                      x1={x(p.endSeconds) + 1}
                      y1={y + 3}
                      x2={x(p.endSeconds) + 1}
                      y2={y + H_ROW - 3}
                      stroke="#b91c1c"
                      strokeWidth={2}
                    >
                      <title>Coupure — {fmtHm(g.seconds)}{"\n"}@ {g.atPlaceName ?? g.atPlaceRaw ?? "lieu inconnu"}</title>
                    </line>
                  );
                })}

              {r.releveAtSeconds.map((at, k) => (
                <polygon
                  key={`rl${k}`}
                  points={diamondPoints(x(at), y + H_ROW / 2)}
                  fill="#15803d"
                  stroke="#fff"
                  strokeWidth={0.5}
                >
                  <title>Relève @ {fmtHm(at)}</title>
                </polygon>
              ))}
            </g>
          );
        })}
      </svg>

      <div style={{ display: "flex", gap: 16, padding: "6px 10px", fontSize: "0.68rem", color: "var(--sentinelle-muted)", flexWrap: "wrap" }}>
        <Legend swatch="#2b6cb0" label="course (couleur de ligne)" />
        <Legend swatch="#c7ccd1" label="déplacement à vide" dashed />
        <Legend swatch="#d97706" label="réserve tactique" />
        <Legend swatch="#b91c1c" label={`| coupure (≥ ${formatDuration(thresholds.driverSplitShiftSeconds)})`} />
        <Legend swatch="#15803d" label="◆ relève" />
      </div>
    </div>
  );
}

/**
 * Pop-in de détail d'UNE prestation (dump 2026-09-10) — déclenché par le clic
 * sur sa barre dans le Gantt, remplace l'ancienne infobulle SVG native (`title`,
 * conservée comme repli au survol). Ne montre que ce que porte déjà
 * `ServiceTimelinePrestation` — lieux/destination complets restent dans le
 * plan détaillé du service (colonne de droite).
 */
function PrestationPopin({
  row,
  prestation,
  onClose,
}: {
  row: ServiceTimelineRow;
  prestation: ServiceTimelinePrestation;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-label="Détail de la prestation"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,24,30,.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--sentinelle-surface)",
          border: "1px solid var(--sentinelle-rule)",
          borderRadius: 6,
          padding: 16,
          minWidth: 260,
          boxShadow: "0 10px 30px rgba(0,0,0,.3)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
          <strong style={{ font: "700 13px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)" }}>
            {labelPrestation(prestation)}
          </strong>
          <button
            type="button"
            className="button is-small is-white"
            aria-label="Fermer"
            onClick={onClose}
            style={{ height: 30, width: 30, padding: 0, lineHeight: 1, flex: "none" }}
          >
            ✕
          </button>
        </div>
        <p className="mono" style={{ fontSize: "0.78rem", margin: "2px 0" }}>
          Service {row.groupLetters} {row.serviceNumber}
        </p>
        {prestation.startSeconds != null && prestation.endSeconds != null ? (
          <p className="mono" style={{ fontSize: "0.78rem", margin: "2px 0" }}>
            {fmtHm(prestation.startSeconds)} → {fmtHm(prestation.endSeconds)} ({fmtHm(prestation.endSeconds - prestation.startSeconds)})
          </p>
        ) : null}
        {prestation.sortieRaw ? (
          <p className="mono" style={{ fontSize: "0.78rem", margin: "2px 0" }}>
            Sortie {prestation.sortieRaw}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function segColor(p: ServiceTimelinePrestation): string {
  if (p.kind?.startsWith("course")) return p.lineColor ?? "#6d7b8a";
  if (p.kind === "reserve-tactique") return "#d97706";
  return "#c7ccd1";
}

function Legend({ swatch, label, dashed }: { swatch: string; label: string; dashed?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span
        style={{
          width: 14,
          height: 10,
          background: swatch,
          borderRadius: 2,
          border: dashed ? "1px dashed var(--sentinelle-rule)" : "none",
        }}
      />
      {label}
    </span>
  );
}

// ── Détail d'un service ────────────────────────────────────────────────────

function ServiceDetail({ plan, isToday }: { plan: ServicePlanResponse; isToday: boolean }) {
  const gapByIndex = useMemo(() => new Map(plan.gaps.map((g) => [g.afterIndex, g])), [plan.gaps]);
  return (
    // Panneau latéral (split diagramme/détail, dump 2026-09-10) : contour
    // complet plutôt que la séparation horizontale de l'ancien layout empilé.
    <section
      style={{
        border: "1px solid var(--sentinelle-rule)",
        borderTop: "2px solid var(--sentinelle-rail)",
        background: "#fff",
        padding: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        <h3 style={{ font: "700 14px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)" }}>
          Service {plan.groupLetters} {plan.serviceNumber}
        </h3>
        <span className="mono" style={{ fontSize: "0.72rem", color: "var(--sentinelle-muted)" }}>
          amplitude {plan.startSeconds != null ? fmtHm(plan.startSeconds) : "—"}–
          {plan.endSeconds != null ? fmtHm(plan.endSeconds) : "—"} · travail{" "}
          {plan.workTimeSeconds != null ? fmtHm(plan.workTimeSeconds) : "—"} · {plan.date}
        </span>
      </div>

      {/* Tableau large (7 colonnes) : défilement horizontal plutôt qu'illisible
          sous ~400px (demande utilisateur 2026-09-11), y compris dans le tiroir mobile. */}
      <div style={{ overflowX: "auto" }}>
      <table className="table is-fullwidth is-narrow" style={{ fontSize: "0.72rem" }}>
        <thead>
          <tr>
            <th>Début</th>
            <th>Fin</th>
            <th>Ligne</th>
            <th>Sortie</th>
            <th>Type</th>
            <th>De → vers</th>
            <th>Destination</th>
          </tr>
        </thead>
        <tbody>
          {plan.prestations.map((p, i) => {
            const gap = gapByIndex.get(i);
            return (
              <Fragment key={i}>
                <tr>
                  <td className="mono">{p.startSeconds != null ? fmtHm(p.startSeconds) : "—"}</td>
                  <td className="mono">{p.endSeconds != null ? fmtHm(p.endSeconds) : "—"}</td>
                  <td className="mono" style={p.lineColor ? { borderLeft: `3px solid ${p.lineColor}`, paddingLeft: 5 } : undefined}>
                    {p.ligneRaw}
                  </td>
                  <td className="mono">{p.sortieRaw ?? "—"}</td>
                  <td>{prettyKind(p.kind)}</td>
                  <td className="mono">
                    {(p.startPlaceName ?? p.startPlaceRaw ?? "—") + " → " + (p.endPlaceName ?? p.endPlaceRaw ?? "—")}
                  </td>
                  <td>{p.destinationName ?? p.destinationRaw ?? "—"}</td>
                </tr>
                {gap ? (
                  <tr>
                    <td colSpan={7} style={{ background: "var(--sentinelle-surface)", color: "var(--sentinelle-muted)", fontStyle: "italic" }}>
                      ↳ {gapLabel(gap)} — {fmtHm(gap.seconds)}
                      {gap.atPlaceName || gap.atPlaceRaw ? ` @ ${gap.atPlaceName ?? gap.atPlaceRaw}` : ""}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      </div>

      {plan.releves.length > 0 ? (
        <>
          <h4 style={{ font: "700 12px/1.2 var(--bulma-family-secondary)", margin: "10px 0 4px" }}>Relèves</h4>
          <ul style={{ fontSize: "0.72rem", listStyle: "disc", paddingLeft: 18 }}>
            {plan.releves.map((r, i) => (
              <li key={i} className="mono">
                {fmtHm(r.atSeconds)} — {r.direction === "in" ? "reprend" : "passe la main"} · sortie {r.sortieRaw} ·{" "}
                {r.direction === "in" ? "de" : "à"} {r.withGroupLetters}
                {r.withServiceNumber} @ {r.placeName || r.placeRaw}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h4 style={{ font: "700 12px/1.2 var(--bulma-family-secondary)", margin: "10px 0 4px" }}>
        Recoupement temps réel {isToday ? "" : "(journée passée — sans objet)"}
      </h4>
      {plan.live.journeys.length === 0 ? (
        <p className="has-text-grey is-size-7">Aucune course active ne porte ce n° de service.</p>
      ) : (
        <table className="table is-narrow" style={{ fontSize: "0.72rem" }}>
          <thead>
            <tr>
              <th>N° bus</th>
              <th>Ligne</th>
              <th>Sortie</th>
              <th style={{ textAlign: "right" }}>Écart</th>
              <th>Position</th>
            </tr>
          </thead>
          <tbody>
            {plan.live.journeys.map((j) => (
              <tr key={j.journeyId}>
                <td className="mono">{j.vehicleDisplayNumber ?? "—"}</td>
                <td className="mono">{j.lineCode}</td>
                <td className="mono">{j.runId ?? "—"}</td>
                <td className="mono" style={{ textAlign: "right" }}>
                  {formatDeviation(j.deviationSeconds)}
                </td>
                <td>{j.positionType}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="has-text-grey" style={{ fontSize: "0.66rem", marginTop: 6 }}>
        Recoupement approché : par n° de service seul (le n° n'est pas unique — cf. contrat §12.12).
      </p>
    </section>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtHm(seconds: number): string {
  const m = Math.round(seconds / 60);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(((m % 60) + 60) % 60).padStart(2, "0")}`;
}

function diamondPoints(cx: number, cy: number): string {
  const r = 4;
  return `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
}

function labelPrestation(p: ServiceTimelinePrestation): string {
  if (p.kind?.startsWith("course")) return `Ligne ${p.ligneRaw}${p.sortieRaw ? ` (${p.sortieRaw})` : ""}`;
  return prettyKind(p.kind);
}

/** Abréviation d'un type de prestation à afficher DANS la barre (J.10). */
function segShort(kind: string | null): string {
  switch (kind) {
    case "deplacement":
      return "HLP";
    case "ramassage":
      return "RAM";
    case "reserve-tactique":
      return "RÉS";
    default:
      return "";
  }
}

/**
 * Infobulle riche d'une prestation (J.10) — plusieurs lignes : intitulé (avec
 * sortie), puis créneau + durée. `\n` : rendu multiligne par le navigateur dans
 * le tooltip SVG natif. Le détail lieu/destination reste dans le tableau du
 * service sélectionné (`/services/{group}/{number}`).
 */
function titlePrestation(p: ServiceTimelinePrestation): string {
  const head = labelPrestation(p);
  if (p.startSeconds == null || p.endSeconds == null) return head;
  return `${head}\n${fmtHm(p.startSeconds)} → ${fmtHm(p.endSeconds)}  (${fmtHm(p.endSeconds - p.startSeconds)})`;
}

function prettyKind(kind: string | null): string {
  switch (kind) {
    case "course":
    case "course-with-destination":
      return "Course";
    case "deplacement":
      return "Déplacement";
    case "ramassage":
      return "Ramassage";
    case "reserve-tactique":
      return "Réserve tactique";
    default:
      return kind ?? "—";
  }
}

function gapLabel(gap: ServiceGap): string {
  switch (gap.kind) {
    case "coupure":
      return "Coupure";
    // v1.53 — vocabulaire aligné sur le métier : le trou entre la fin d'une
    // (ligne, sortie) et la prestation suivante EST une pause, distincte du
    // battement (même sortie) et du changement-de-sortie (transfert < seuil).
    case "pause":
      return "Pause";
    case "changement-de-sortie":
      return "Changement de sortie";
    default:
      return "Battement";
  }
}
